import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { environment } from '../../environments/environments';
import {
  ApiResponse,
  AssessmentDto, AssessmentRequest, AssessmentStatusRequest,
  QuestionBankItemDto, QuestionBankItemRequest,
  AnswerOptionDto, AnswerOptionRequest,
  AssessmentQuestionDto, AssessmentQuestionRequest,
  LearnerAssessmentResultDto,
  AssessmentScopeDto, AssessmentScopeRequest,
  AssessmentLoMappingDto, AssessmentLoMappingRequest,
  LearnerAttemptQuestionDto, LearnerQuestionResponseDto,
  LearnerFileQuestionResponseDto,
  HomeworkAttachmentDto,
  CourseGradebookResponse,
  ExamCorrectionSummaryDto,
  ExamCorrectionLearnerCaseDto
} from '../models/instructor.models';

@Injectable({ providedIn: 'root' })
export class AssessmentService
{
  private baseUrl = (environment as any).assessmentBaseUrl || (environment.baseUrl + environment.assessment_url);

  constructor(private http: HttpClient) {}

  // --- Assessments ---

  getAll(): Observable<AssessmentDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentDto[]>>(`${this.baseUrl}/api/v1/assessments/all`)
      .pipe(map(res => res.data ?? []));
  }

  getMy(): Observable<AssessmentDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentDto[]>>(
        `${this.baseUrl}/api/v1/assessments/my`)
      .pipe(map(res => res.data ?? []));
  }

  getMyHomework(): Observable<AssessmentDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentDto[]>>(
        `${this.baseUrl}/api/v1/assessments/my-homework`)
      .pipe(map(res => res.data ?? []));
  }

  getByCourse(courseId: string): Observable<AssessmentDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentDto[]>>(
        `${this.baseUrl}/api/v1/assessments/course/${courseId}`)
      .pipe(map(res => res.data ?? []));
  }

  getById(id: string): Observable<AssessmentDto>
  {
    return this.http
      .get<ApiResponse<AssessmentDto>>(`${this.baseUrl}/api/v1/assessments/${id}`)
      .pipe(map(res => res.data));
  }

  create(req: AssessmentRequest): Observable<AssessmentDto>
  {
    return this.http
      .post<ApiResponse<AssessmentDto>>(`${this.baseUrl}/api/v1/assessments/save`, req)
      .pipe(map(res => res.data));
  }

  update(id: string, req: AssessmentRequest): Observable<AssessmentDto>
  {
    return this.http
      .put<ApiResponse<AssessmentDto>>(
        `${this.baseUrl}/api/v1/assessments/${id}`, req)
      .pipe(map(res => res.data));
  }

  updateStatus(id: string, req: AssessmentStatusRequest): Observable<AssessmentDto>
  {
    return this.http
      .patch<ApiResponse<AssessmentDto>>(
        `${this.baseUrl}/api/v1/assessments/${id}/status`, req)
      .pipe(map(res => res.data));
  }

  delete(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/assessments/delete/${id}`);
  }

  // --- Question Bank ---

  getMyQuestions(): Observable<QuestionBankItemDto[]>
  {
    return this.http
      .get<ApiResponse<QuestionBankItemDto[]>>(
        `${this.baseUrl}/api/v1/question-bank/my`)
      .pipe(map(res => res.data ?? []));
  }

  getQuestionsByCourseIds(courseIds: string[]): Observable<QuestionBankItemDto[]>
  {
    const normalizedCourseIds = courseIds
      .map((courseId) => courseId?.trim())
      .filter((courseId): courseId is string => Boolean(courseId));

    if (!normalizedCourseIds.length) {
      return of([]);
    }

    const query = normalizedCourseIds
      .map((courseId) => `courseIds=${encodeURIComponent(courseId)}`)
      .join('&');

    return this.http
      .get<ApiResponse<QuestionBankItemDto[]>>(
        `${this.baseUrl}/api/v1/question-bank/by-courses?${query}`)
      .pipe(map(res => res.data ?? []));
  }

  getQuestionsByLo(loId: string): Observable<QuestionBankItemDto[]>
  {
    return this.http
      .get<ApiResponse<QuestionBankItemDto[]>>(
        `${this.baseUrl}/api/v1/question-bank/lo/${loId}`)
      .pipe(map(res => res.data ?? []));
  }

  getQuestionsByLesson(lessonId: string): Observable<QuestionBankItemDto[]>
  {
    return this.http
      .get<ApiResponse<QuestionBankItemDto[]>>(
        `${this.baseUrl}/api/v1/question-bank/lesson/${lessonId}`)
      .pipe(map(res => res.data ?? []));
  }

  getQuestionById(id: string): Observable<QuestionBankItemDto>
  {
    return this.http
      .get<ApiResponse<QuestionBankItemDto>>(
        `${this.baseUrl}/api/v1/question-bank/${id}`)
      .pipe(map(res => res.data));
  }

  createQuestion(req: QuestionBankItemRequest): Observable<QuestionBankItemDto>
  {
    return this.http
      .post<ApiResponse<QuestionBankItemDto>>(
        `${this.baseUrl}/api/v1/question-bank/save`, req)
      .pipe(map(res => res.data));
  }

  updateQuestion(id: string, req: QuestionBankItemRequest): Observable<QuestionBankItemDto>
  {
    return this.http
      .put<ApiResponse<QuestionBankItemDto>>(
        `${this.baseUrl}/api/v1/question-bank/update/${id}`, req)
      .pipe(map(res => res.data));
  }

  uploadQuestionImage(id: string, file: File): Observable<QuestionBankItemDto>
  {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<ApiResponse<QuestionBankItemDto>>(
        `${this.baseUrl}/api/v1/question-bank/${id}/image`, formData)
      .pipe(map(res => res.data));
  }

  removeQuestionImage(id: string): Observable<QuestionBankItemDto>
  {
    return this.http
      .delete<ApiResponse<QuestionBankItemDto>>(
        `${this.baseUrl}/api/v1/question-bank/${id}/image`)
      .pipe(map(res => res.data));
  }

  deleteQuestion(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/question-bank/delete/${id}`);
  }

  // --- Answer Options ---

  getAnswerOptions(questionId: string): Observable<AnswerOptionDto[]>
  {
    return this.http
      .get<ApiResponse<AnswerOptionDto[]>>(
        `${this.baseUrl}/api/v1/question-answer-options/question/${questionId}`)
      .pipe(map(res => res.data ?? []));
  }

  createAnswerOption(req: AnswerOptionRequest): Observable<AnswerOptionDto>
  {
    return this.http
      .post<ApiResponse<AnswerOptionDto>>(
        `${this.baseUrl}/api/v1/question-answer-options/save`, req)
      .pipe(map(res => res.data));
  }

  updateAnswerOption(id: string, req: AnswerOptionRequest): Observable<AnswerOptionDto>
  {
    return this.http
      .put<ApiResponse<AnswerOptionDto>>(
        `${this.baseUrl}/api/v1/question-answer-options/update/${id}`, req)
      .pipe(map(res => res.data));
  }

  deleteAnswerOption(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/question-answer-options/delete/${id}`);
  }

  // --- Assessment Questions (ASSIGNMENT type only) ---

  getAssessmentQuestions(assessmentId: string): Observable<AssessmentQuestionDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentQuestionDto[]>>(
        `${this.baseUrl}/api/v1/assessment-questions/assessment/${assessmentId}`)
      .pipe(map(res => res.data ?? []));
  }

  addQuestionToAssessment(req: AssessmentQuestionRequest): Observable<AssessmentQuestionDto>
  {
    return this.http
      .post<ApiResponse<AssessmentQuestionDto>>(
        `${this.baseUrl}/api/v1/assessment-questions/save`, req)
      .pipe(map(res => res.data));
  }

  updateQuestionInAssessment(id: string, req: AssessmentQuestionRequest): Observable<AssessmentQuestionDto>
  {
    return this.http
      .put<ApiResponse<AssessmentQuestionDto>>(
        `${this.baseUrl}/api/v1/assessment-questions/update/${id}`, req)
      .pipe(map(res => res.data));
  }

  removeQuestionFromAssessment(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/assessment-questions/delete/${id}`);
  }

  // --- Learner Assessment Results (instructor endpoints) ---

  getResultsByAssessment(assessmentId: string): Observable<LearnerAssessmentResultDto[]>
  {
    return this.http
      .get<ApiResponse<LearnerAssessmentResultDto[]>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/assessment/${assessmentId}/instructor`)
      .pipe(map(res => res.data ?? []));
  }

  getSubmissionInstructor(id: string): Observable<LearnerAssessmentResultDto>
  {
    return this.http
      .get<ApiResponse<LearnerAssessmentResultDto>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/${id}/instructor`)
      .pipe(map(res => res.data));
  }

  getCorrectionSummary(instructorId: string): Observable<ExamCorrectionSummaryDto[]>
  {
    return this.http
      .get<ApiResponse<ExamCorrectionSummaryDto[]>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/instructor/${instructorId}/exam-corrections`)
      .pipe(map(res => res.data ?? []));
  }

  getCorrectionCases(
    instructorId: string,
    assessmentId: string,
  ): Observable<ExamCorrectionLearnerCaseDto[]>
  {
    return this.http
      .get<ApiResponse<ExamCorrectionLearnerCaseDto[]>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/instructor/${instructorId}/exam-corrections/${assessmentId}`)
      .pipe(map(res => res.data ?? []));
  }

  // --- Learner Attempt Questions ---

  getAttemptQuestions(resultId: string): Observable<LearnerAttemptQuestionDto[]>
  {
    return this.http
      .get<ApiResponse<LearnerAttemptQuestionDto[]>>(
        `${this.baseUrl}/api/v1/learner-attempt-questions/result/${resultId}`)
      .pipe(map(res => res.data ?? []));
  }

  // --- Learner Question Responses ---

  getResponsesByResult(
    resultId: string,
    markerRole?: 'FIRST_MARKER' | 'SECOND_MARKER'
  ): Observable<LearnerQuestionResponseDto[]>
  {
    const suffix = markerRole ? `?markerRole=${markerRole}` : '';
    return this.http
      .get<ApiResponse<LearnerQuestionResponseDto[]>>(
        `${this.baseUrl}/api/v1/learner-question-responses/result/${resultId}${suffix}`)
      .pipe(map(res => res.data ?? []));
  }

  createResponse(
    payload: {
      resultId: string;
      questionBankItemId: string;
      learnerId: string;
      selectedOptionId?: string | null;
      selectedOptionIds?: string[] | null;
      answerText?: string | null;
      isCorrect?: boolean | null;
      score?: number | null;
      timeSpentSeconds?: number | null;
    }
  ): Observable<LearnerQuestionResponseDto>
  {
    return this.http
      .post<ApiResponse<LearnerQuestionResponseDto>>(
        `${this.baseUrl}/api/v1/learner-question-responses/save`, payload)
      .pipe(map(res => res.data));
  }

  updateResponse(
    id: string,
    payload: { score: number; isCorrect: boolean; markerRole: 'FIRST_MARKER' | 'SECOND_MARKER' }
  ): Observable<LearnerQuestionResponseDto>
  {
    return this.http
      .put<ApiResponse<LearnerQuestionResponseDto>>(
        `${this.baseUrl}/api/v1/learner-question-responses/update/${id}`, payload)
      .pipe(map(res => res.data));
  }

  finalizeGrade(
    resultId: string,
    payload?: { feedback?: string | null; gradedByInstructorId?: string | null }
  ): Observable<LearnerAssessmentResultDto>
  {
    return this.http
      .post<ApiResponse<LearnerAssessmentResultDto>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/${resultId}/finalize-grade`,
        payload ?? {})
      .pipe(map(res => res.data));
  }

  saveMarkerReview(
    resultId: string,
    payload: {
      markerRole: 'FIRST_MARKER' | 'SECOND_MARKER';
      instructorId: string;
      feedback?: string | null;
      submit: boolean;
    }
  ): Observable<LearnerAssessmentResultDto>
  {
    return this.http
      .post<ApiResponse<LearnerAssessmentResultDto>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/${resultId}/marker-review`,
        payload)
      .pipe(map(res => res.data));
  }

  approveRetakeRequest(
    resultId: string,
    payload?: { instructorId?: string | null }
  ): Observable<LearnerAssessmentResultDto>
  {
    return this.http
      .post<ApiResponse<LearnerAssessmentResultDto>>(
        `${this.baseUrl}/api/v1/learner-assessment-results/${resultId}/retake-request/approve`,
        payload ?? {})
      .pipe(map(res => res.data));
  }

  // --- Gradebook ---

  getGradebook(courseId: string): Observable<CourseGradebookResponse>
  {
    return this.http
      .get<ApiResponse<CourseGradebookResponse>>(
        `${this.baseUrl}/api/v1/gradebook/course/${courseId}`)
      .pipe(map(res => res.data));
  }

  // --- Assessment Scopes ---

  getScopesByAssessment(assessmentId: string): Observable<AssessmentScopeDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentScopeDto[]>>(
        `${this.baseUrl}/api/v1/assessment-scopes/assessment/${assessmentId}`)
      .pipe(map(res => res.data ?? []));
  }

  saveScope(req: AssessmentScopeRequest): Observable<AssessmentScopeDto>
  {
    return this.http
      .post<ApiResponse<AssessmentScopeDto>>(
        `${this.baseUrl}/api/v1/assessment-scopes/save`, req)
      .pipe(map(res => res.data));
  }

  deleteScope(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/assessment-scopes/delete/${id}`);
  }

  // --- Assessment LO Mappings ---

  getLOMappingsByAssessment(assessmentId: string): Observable<AssessmentLoMappingDto[]>
  {
    return this.http
      .get<ApiResponse<AssessmentLoMappingDto[]>>(
        `${this.baseUrl}/api/v1/assessment-lo-mappings/assessment/${assessmentId}`)
      .pipe(map(res => res.data ?? []));
  }

  saveLoMapping(req: AssessmentLoMappingRequest): Observable<AssessmentLoMappingDto>
  {
    return this.http
      .post<ApiResponse<AssessmentLoMappingDto>>(
        `${this.baseUrl}/api/v1/assessment-lo-mappings/save`, req)
      .pipe(map(res => res.data));
  }

  deleteLoMapping(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/assessment-lo-mappings/delete/${id}`);
  }

  // --- Homework Attachments (file questions) ---

  getHomeworkAttachments(assessmentId: string): Observable<HomeworkAttachmentDto[]>
  {
    return this.http
      .get<ApiResponse<HomeworkAttachmentDto[]>>(
        `${this.baseUrl}/api/v1/homework-attachments/by-assessment/${assessmentId}`)
      .pipe(map(res => res.data ?? []));
  }

  uploadHomeworkAttachment(
    assessmentId: string,
    file: File,
    marks: number,
    instructionsEn: string,
    allowedSubmissionFormats: string[] = []
  ): Observable<HomeworkAttachmentDto>
  {
    const fd = new FormData();
    fd.append('assessmentId', assessmentId);
    fd.append('marks', String(marks));
    if (instructionsEn) { fd.append('instructionsEn', instructionsEn); }
    allowedSubmissionFormats
      .filter((format) => !!format)
      .forEach((format) => fd.append('allowedSubmissionFormats', format));
    fd.append('file', file);
    return this.http
      .post<ApiResponse<HomeworkAttachmentDto>>(
        `${this.baseUrl}/api/v1/homework-attachments/save`, fd)
      .pipe(map(res => res.data));
  }

  deleteHomeworkAttachment(id: string): Observable<void>
  {
    return this.http
      .delete<void>(`${this.baseUrl}/api/v1/homework-attachments/delete/${id}`);
  }

  // --- Learner File Question Responses ---

  getFileResponsesByResult(
    resultId: string,
    markerRole?: 'FIRST_MARKER' | 'SECOND_MARKER'
  ): Observable<LearnerFileQuestionResponseDto[]>
  {
    const suffix = markerRole ? `?markerRole=${markerRole}` : '';
    return this.http
      .get<ApiResponse<LearnerFileQuestionResponseDto[]>>(
        `${this.baseUrl}/api/v1/learner-file-question-responses/result/${resultId}${suffix}`)
      .pipe(map(res => res.data ?? []));
  }

  updateFileResponse(
    id: string,
    payload: { score: number; isCorrect: boolean; markerRole: 'FIRST_MARKER' | 'SECOND_MARKER' }
  ): Observable<LearnerFileQuestionResponseDto>
  {
    return this.http
      .put<ApiResponse<LearnerFileQuestionResponseDto>>(
        `${this.baseUrl}/api/v1/learner-file-question-responses/update/${id}`, payload)
      .pipe(map(res => res.data));
  }

}
